import { cn } from "@/lib/utils";
import { SUBJECTS } from "./cards";
import { CompletionIndicator } from "./progress";
import { XPBadge } from "./rewards";
import { CaretRight, CheckCircle, Circle, Lock, Clock, Medal, Star } from "@phosphor-icons/react";

/* Container that stacks rows with hairline dividers. */
export function List({ children, className }) {
  return <div className={cn("divide-y divide-border overflow-hidden rounded-3xl border border-border bg-card shadow-soft", className)}>{children}</div>;
}

/* --------------------------------- Lesson List ----------------------------------- */
export function LessonListItem({ index = 1, title = "Counting to 100", state = "done" }) {
  const icon = state === "done" ? CheckCircle : state === "locked" ? Lock : Circle;
  const Icon = icon;
  return (
    <div className={cn("flex items-center gap-4 px-5 py-4", state === "locked" && "opacity-60")}>
      <span className={cn("flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-extrabold",
        state === "done" ? "bg-teal/15 text-teal-ink" : state === "current" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
        {state === "done" ? <Icon size={20} weight="fill" /> : index}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{title}</p>
        <p className="text-sm font-semibold text-muted-foreground capitalize">{state === "current" ? "Up next" : state}</p>
      </div>
      {state !== "locked" && <CaretRight size={18} weight="bold" className="text-muted-foreground" />}
    </div>
  );
}

/* -------------------------------- Homework List ---------------------------------- */
export function HomeworkListItem({ title = "Spelling list 4", due = "Fri", subject = "english", done = false }) {
  const s = SUBJECTS[subject];
  return (
    <div className="flex items-center gap-4 px-5 py-4">
      <span className={cn("flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl", s.tint, s.text)}>
        <s.icon size={22} weight="fill" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{title}</p>
        <CompletionIndicator state={done ? "complete" : "in-progress"} label={done ? "Handed in" : `Due ${due}`} />
      </div>
      <span className="flex items-center gap-1 text-sm font-bold text-muted-foreground"><Clock size={15} weight="bold" /> {due}</span>
    </div>
  );
}

/* ------------------------------- Achievement List -------------------------------- */
export function AchievementListItem({ title = "Read 5 stories", xp = 30, color = "secondary", unlocked = true }) {
  return (
    <div className={cn("flex items-center gap-4 px-5 py-4", !unlocked && "opacity-70")}>
      <span className={cn("flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full",
        unlocked ? "bg-secondary/25 text-secondary-ink" : "bg-muted text-muted-foreground")}>
        {unlocked ? <Medal size={22} weight="fill" /> : <Lock size={18} weight="bold" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{title}</p>
        <p className="text-sm font-semibold text-muted-foreground">{unlocked ? "Unlocked" : "Keep going!"}</p>
      </div>
      <XPBadge amount={xp} size="sm" />
    </div>
  );
}

/* ----------------------------------- Topic List ---------------------------------- */
export function TopicListItem({ title = "Fractions", subject = "maths", count = 8 }) {
  const s = SUBJECTS[subject];
  return (
    <button className="group flex w-full items-center gap-4 px-5 py-4 text-left">
      <span className={cn("flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl", s.tint, s.text)}>
        <s.icon size={22} weight="fill" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold">{title}</p>
        <p className="text-sm font-semibold text-muted-foreground">{count} lessons · {s.label}</p>
      </div>
      <Star size={18} weight="fill" className="text-muted/0 text-secondary opacity-0 transition-opacity group-hover:opacity-100" />
      <CaretRight size={18} weight="bold" className="text-muted-foreground transition-transform group-hover:translate-x-1" />
    </button>
  );
}
